import type { BereavementRelationship, MyRequest, RequestType, TimeOffKind, VoteWindow } from "@scheduler/shared";

/**
 * CHANGE a request (punch list S35, Jeremy's option B, 3 Nov 2026): the request form filled in
 * with an existing request, whose save replaces it through the reschedule rule (rule 16,
 * `supersedesRequestId`). The API checks the replacement as if the old one were gone, approving
 * it cancels the old one, and denying it leaves the old one standing.
 *
 * This is the one place that turns a listed request into the form's starting state, so what the
 * person sees is exactly what they asked for: the type, the dates, the hours on each day when any
 * day was less than a full one, the kind of time off, and the explanation.
 */

/** The kinds the form offers (NewRequest.tsx). Unpaid is HR's and is never changeable here. */
export const CHANGEABLE_KINDS = ["JURY_DUTY", "BEREAVEMENT", "VOLUNTEER", "VOTE"] as const;
export type ChangeableKind = (typeof CHANGEABLE_KINDS)[number];

export interface ChangeStart {
  readonly requestId: string;
  readonly type: RequestType;
  /** The people the replacement is for: the same as the request's, never the caller's by default. */
  readonly resourceIds: readonly string[];
  readonly start: string;
  readonly end: string;
  /** True when any stored day was less than 8 hours; `perDay` then names every stored day. */
  readonly partial: boolean;
  readonly perDay: Readonly<Record<string, number>>;
  readonly kind: ChangeableKind | null;
  readonly relationship: BereavementRelationship | null;
  readonly voteWindow: VoteWindow | null;
  readonly explanation: string;
}

function changeableKind(kind: TimeOffKind | null): ChangeableKind | null {
  return kind && (CHANGEABLE_KINDS as readonly string[]).includes(kind) ? (kind as ChangeableKind) : null;
}

export function changeStart(r: MyRequest): ChangeStart {
  const partial = r.days.some((d) => d.hours !== 8);
  return {
    requestId: r.id,
    type: r.type,
    resourceIds: r.people.map((p) => p.id),
    start: r.start,
    end: r.end,
    partial,
    perDay: partial ? Object.fromEntries(r.days.map((d) => [d.date, d.hours])) : {},
    kind: changeableKind(r.timeOffKind),
    relationship: r.bereavementRelationship,
    voteWindow: r.voteWindow,
    explanation: r.explanation ?? "",
  };
}
